import type { ISearchPageFilter } from '../../../api/types/searchPageApiTypes';
import { ISearchPageFilterValue } from '../../../types';

import { formatNumber } from '../../../helpers/formatting';
import { plural } from '@dist-marketplace-frontend/utils';
import { asString } from '../../../utils/query-parameters';
import { shouldShowTrims } from '../features/makeModel/MakeModel.constants';

const mapTrimValues = (
  trimValues?: string | string[],
): string[] | undefined => {
  if (!trimValues) return undefined;
  return typeof trimValues === 'string'
    ? trimValues
        .split(',')
        .filter(Boolean)
        .map((t) => decodeURIComponent(t))
    : trimValues.map((t) => decodeURIComponent(t)).filter(Boolean);
};

const getSingleOptionDisplay = (
  values: Array<ISearchPageFilterValue>,
  target?: string,
  queryType?: string,
) => {
  const normalizeValue = (value?: string) =>
    value ? value.replace('+', ' ') : '';

  const matchingValue = values
    ? values.find(
        ({ value, type }) =>
          normalizeValue(value) === target &&
          (type === queryType || type === undefined),
      )
    : undefined;

  return matchingValue?.displayName;
};

const getMultiOptionDisplay = (
  values: Array<ISearchPageFilterValue>,
  targets: Array<string>,
) => {
  const matchingDisplayValues = getMatchingValues(values, targets);

  return displayValueWithCount(matchingDisplayValues);
};

const getMatchingValues = (
  values: Array<ISearchPageFilterValue>,
  targets: Array<string>,
) => {
  const matchingDisplayValues = values.filter(({ value }) =>
    targets.some((target) => value === target),
  );

  return matchingDisplayValues;
};

const displayValueWithCount = (values: Array<ISearchPageFilterValue>) => {
  const [firstValue] = values;
  const multipleValuesText = firstValue
    ? `${firstValue.displayName} (+${values.length - 1})`
    : undefined;
  const singleValueText = firstValue ? firstValue.displayName : '';
  const formattedText = `${
    values.length > 1 ? multipleValuesText : singleValueText
  }`;

  return firstValue ? formattedText : undefined;
};

const getQuickFilterMultiOptionDisplayVariant = (
  values: Array<ISearchPageFilterValue>,
  targets: Array<string>,
  name: string,
) => {
  const matchingDisplayValues = getMatchingValues(values, targets);

  return displayValueWithCountVariant(matchingDisplayValues, name);
};

const getFilterMultiOptionDisplayVariant = (
  values: Array<ISearchPageFilterValue>,
  targets: Array<string>,
) => {
  const matchingDisplayValues = getMatchingValues(values, targets);

  const displayValues = matchingDisplayValues
    .map(({ displayName }) => displayName)
    .join(', ');

  return displayValues;
};

const displayValueWithCountVariant = (
  values: Array<ISearchPageFilterValue>,
  name: string,
) => {
  const [firstValue] = values;
  const multipleValuesText = `${values.length} ${name}s`;
  const singleValueText = firstValue ? firstValue.displayName : '';
  const formattedText = `${
    values.length > 1 ? multipleValuesText : singleValueText
  }`;

  return firstValue ? formattedText : undefined;
};

const getPriceDisplay = (
  args: {
    price_from?: string;
    price_to?: string;
    sterlingPrice_from?: string;
    sterlingPrice_to?: string;
  },
  labels: { from?: string; to?: string } = { from: 'Min', to: 'Max' },
) => {
  const { price_from, price_to, sterlingPrice_from, sterlingPrice_to } = args;

  if (price_from || price_to) {
    const formattedFrom = formatPrice('€', price_from);
    const formattedTo = formatPrice('€', price_to);

    return getRangeDisplay({ from: formattedFrom, to: formattedTo }, labels);
  } else if (sterlingPrice_from || sterlingPrice_to) {
    const formattedFrom = formatPrice('£', sterlingPrice_from);
    const formattedTo = formatPrice('£', sterlingPrice_to);

    return getRangeDisplay({ from: formattedFrom, to: formattedTo }, labels);
  } else return undefined;
};

export const formatPrice = (currency: '€' | '£', value?: string) => {
  return value ? `${currency}${formatNumber(parseInt(value))}` : undefined;
};

const getRangeDisplay = (
  args: { from?: string; to?: string },
  labels: { from?: string; to?: string } = { from: 'Min', to: 'Max' },
) => {
  const { from, to } = args;
  const { from: fromLabel = 'Min', to: toLabel = 'Max' } = labels;

  if (from && to) {
    if (from === to) {
      return from;
    } else return `${from} - ${to}`;
  } else if (from) {
    return `${fromLabel} ${from}`;
  } else if (to) {
    return `${toLabel} ${to}`;
  } else return undefined;
};

const formatMakeAndModelQueries = (makeQuery?: string | Array<string>) => {
  if (typeof makeQuery === 'string') {
    const { make, models } = getMakeAndModelValues(makeQuery);
    return `${make}${formatModels(models)}`;
  } else if (Array.isArray(makeQuery)) {
    let modelAggregate: Array<string> = [];

    makeQuery.forEach((query) => {
      const { models } = getMakeAndModelValues(query);
      modelAggregate.push(...models);
    });

    return `${makeQuery.length} Makes${formatModels(modelAggregate)}`;
  } else return undefined;
};

const formatMakeModelTrimQueries = (
  makeQuery?: string | Array<string>,
  sectionName?: string,
) => {
  if (typeof makeQuery === 'string') {
    const { make, models, trims } = getMakeAndModelValues(makeQuery);

    const makeCount = 1;
    const modelCount = models.length;
    const trimCount = trims.length;

    return formatMakeModelTrimText(
      make,
      models,
      trims,
      makeCount,
      modelCount,
      trimCount,
      sectionName,
    );
  } else if (Array.isArray(makeQuery)) {
    // Use Set to automatically deduplicate makes (e.g., multiple "Volkswagen" entries count as one)
    const uniqueMakes = new Set<string>();
    let modelAggregate: Array<string> = [];
    let trimAggregate: Array<string> = [];

    makeQuery.forEach((query) => {
      const { make, models, trims } = getMakeAndModelValues(query);
      uniqueMakes.add(make);
      modelAggregate.push(...models);
      trimAggregate.push(...trims);
    });

    const makeCount = uniqueMakes.size;
    const modelCount = modelAggregate.length;
    const trimCount = trimAggregate.length;

    // Get the first make name if there's only one unique make
    const makeName = makeCount === 1 ? Array.from(uniqueMakes)[0] : '';

    return formatMakeModelTrimText(
      makeName,
      modelAggregate,
      trimAggregate,
      makeCount,
      modelCount,
      trimCount,
      sectionName,
    );
  } else return undefined;
};

const formatMakeModelTrimText = (
  make: string,
  models: string[],
  trims: string[],
  makeCount: number,
  modelCount: number,
  trimCount: number,
  sectionName?: string,
): string => {
  const result: string[] = [];

  const makeText = makeCount === 1 ? make : `${makeCount} Makes`;
  result.push(makeText);

  const shouldShowModelName = makeCount === 1 && modelCount === 1;
  if (shouldShowModelName) {
    result.push(models[0]);
  } else if (modelCount > 0) {
    result.push(`${modelCount} Models`);
  }

  // Only show trim information for cars section
  const showTrimsForSection = shouldShowTrims(sectionName);
  if (showTrimsForSection) {
    const shouldShowTrimName =
      makeCount === 1 && modelCount === 1 && trimCount === 1;
    if (shouldShowTrimName) {
      result.push(trims[0]);
    } else if (trimCount > 0) {
      result.push(`${trimCount} ${plural('Trim', 'Trims', trimCount)}`);
    }
  }

  const useCommas =
    result.length > 1 && (makeCount > 1 || modelCount > 1 || trimCount > 1);
  return useCommas ? result.join(', ') : result.join(' ');
};

const getMakeAndModelValues = (makesAndModelsQueryValue: string) => {
  const makeModelTrimQuery = makesAndModelsQueryValue.split(';');
  const make = makeModelTrimQuery[0];

  const modelQuery = makeModelTrimQuery.find((query) =>
    query.startsWith('model:'),
  );
  const trimQuery = makeModelTrimQuery.find((query) =>
    query.startsWith('trim:'),
  );

  const models = modelQuery ? modelQuery.replace('model:', '').split(',') : [];
  const trims = trimQuery
    ? trimQuery
        .replace('trim:', '')
        .split(',')
        .map((t) => decodeURIComponent(t))
    : [];

  return { make, models, trims };
};

const formatModels = (models: Array<string>) => {
  const plural = models.length > 1 ? 's' : '';
  return models.length > 0 ? `, ${models.length} Model${plural}` : '';
};
const formatMakeAndModelForFilters = (
  makeQuery: string,
  models: string[] = [],
  trims: string[] = [],
): string => {
  if (
    typeof makeQuery === 'string' &&
    (makeQuery.includes(';model:') || makeQuery.includes(';trim:'))
  ) {
    const { make, models, trims } = getMakeAndModelValues(makeQuery);
    return formatMakeAndModelForFilters(make, models, trims);
  }

  if (trims.length) {
    const [firstModel] = models;
    const [firstTrim, ...remainingTrims] = trims;

    if (firstModel && firstTrim) {
      const formattedMakeModelTrim = `${makeQuery} ${firstModel} ${firstTrim}`;

      if (remainingTrims.length > 0) {
        return `${formattedMakeModelTrim} (+${remainingTrims.length})`;
      }

      return formattedMakeModelTrim;
    }
  }

  if (!models.length) {
    return makeQuery;
  }

  const [firstModel, ...remainingModels] = models;
  const formattedMakeModel = `${makeQuery} ${firstModel}`;

  if (!remainingModels.length) {
    return formattedMakeModel;
  }

  return `${formattedMakeModel} (+${remainingModels.length})`;
};

const formatMakeAndModelQueriesForFilters = (makeQuery?: string | string[]) => {
  if (typeof makeQuery === 'string') {
    const { make, models, trims } = getMakeAndModelValues(makeQuery);
    return formatMakeAndModelForFilters(make, models, trims);
  }

  if (Array.isArray(makeQuery)) {
    const formattedEntries = makeQuery
      .map((entry) => formatMakeAndModelForFilters(entry))
      .filter(Boolean);

    const additionalMakesCount = makeQuery.length - formattedEntries.length;

    if (additionalMakesCount > 0) {
      formattedEntries.push(`(+${additionalMakesCount})`);
    }

    return formattedEntries;
  }

  return undefined;
};

const formatTrimForPathParam = (trim?: string | string[]): string => {
  if (!trim) return '';

  const trimValues = mapTrimValues(trim);
  if (!trimValues || trimValues.length === 0) return '';

  const [first, ...rest] = trimValues;
  const additionalCount = rest.length > 0 ? ` (+${rest.length})` : '';
  return ` ${first}${additionalCount}`;
};

const validateFilterValue = (
  filterName: string,
  filterValue: string,
  filtersData: Array<ISearchPageFilter>,
) => {
  let filter = filtersData.find(({ name }) => name === filterName);

  if (filter?.type === 'group' && filter?.subFilters) {
    filter = filter.subFilters.find(({ name }) => name === filterName);
  }

  const values = filter?.values;
  const group = filter?.searchQueryGroup;
  const toValues = filter?.values?.[0]?.to;
  const fromValues = filter?.values?.[0]?.from;

  if (group === 'filters') {
    return values?.find(({ value }) => value === filterValue)?.value;
  }
  if (group === 'ranges') {
    const to = toValues?.find(({ value }) => value === filterValue)?.value;
    const from = fromValues?.find(({ value }) => value === filterValue)?.value;
    const fallback = values?.find(({ value }) => value === filterValue)?.value;

    return to ?? from ?? fallback;
  }
};

const getFilterValue = (
  filtersData: Array<ISearchPageFilter>,
  name: string,
  value?: string | Array<string>,
  formattedFilterName?: string,
  filterValue?: string,
) => {
  if (filterValue && name === formattedFilterName) {
    return [filterValue];
  }
  if (value) {
    if (Array.isArray(value)) {
      return value.flatMap(
        (item) => validateFilterValue(name, item, filtersData) ?? [],
      );
    }
    const validatedValue = validateFilterValue(name, value, filtersData);
    return validatedValue ? [validatedValue] : undefined;
  }
};

const getRangeValue = (
  filtersData: Array<ISearchPageFilter>,
  name: string,
  fromValue?: string | Array<string>,
  toValue?: string | Array<string>,
  formattedFilterName?: string,
  filterValue?: string,
) => {
  if (filterValue && name === formattedFilterName) {
    return [filterValue];
  }

  if (fromValue || toValue) {
    const from = asString(fromValue);
    const to = asString(toValue);
    const validatedFrom = from && validateFilterValue(name, from, filtersData);
    const validatedTo = to && validateFilterValue(name, to, filtersData);

    return validatedFrom || validatedTo
      ? [
          ...(validatedFrom ? [validatedFrom] : []),
          ...(validatedTo ? [validatedTo] : []),
        ]
      : undefined;
  }
};

const formatWarrantyDuration = (
  warrantyDuration?: string | Array<string>,
  verifications?: string | Array<string>,
) => {
  const warrantyText = formatWarranty(warrantyDuration);
  const verificationText = formatVerifications(verifications);

  if (warrantyText && verificationText) {
    return `${warrantyText}, ${verificationText}`;
  }

  return warrantyText || verificationText || undefined;
};

const formatWarranty = (warrantyDuration?: string | Array<string>): string => {
  if (!warrantyDuration) return '';

  const durations = Array.isArray(warrantyDuration)
    ? warrantyDuration
    : [warrantyDuration];

  const formattedDurations = durations
    .map(formatSingleWarranty)
    .filter(Boolean);

  if (formattedDurations.length === 0) return '';
  if (formattedDurations.length === 1) return formattedDurations[0];

  return `${formattedDurations[0]} (+${formattedDurations.length - 1})`;
};

const formatSingleWarranty = (duration: string): string => {
  const months = parseInt(duration, 10);

  if (isNaN(months) || months <= 0) return '';

  // Convert to years if it's a multiple of 12
  if (months >= 12 && months % 12 === 0) {
    const years = months / 12;
    const yearText = plural('year', 'years', years);
    return `${years} ${yearText}+ Warranty`;
  }

  const monthText = plural('month', 'months', months);
  return `${months} ${monthText}+ Warranty`;
};

const formatVerifications = (
  verifications?: string | Array<string>,
): string => {
  if (!verifications) return '';

  const count = Array.isArray(verifications) ? verifications.length : 1;
  return `${count} Verification${count === 1 ? '' : 's'}`;
};

const formatPriceForQuickFilters = (
  subFilterValues?: (string | undefined)[],
) => {
  if (!subFilterValues?.length) return undefined;

  if (subFilterValues.length > 1) {
    return `${subFilterValues.length} Price filters`;
  }

  return subFilterValues[0];
};

export {
  getSingleOptionDisplay,
  getMultiOptionDisplay,
  getQuickFilterMultiOptionDisplayVariant,
  getFilterMultiOptionDisplayVariant,
  getPriceDisplay,
  getRangeDisplay,
  formatMakeAndModelQueries,
  formatMakeModelTrimQueries,
  formatMakeAndModelQueriesForFilters,
  formatMakeAndModelForFilters,
  getFilterValue,
  getRangeValue,
  validateFilterValue,
  formatWarrantyDuration,
  formatTrimForPathParam,
  formatPriceForQuickFilters,
  mapTrimValues,
};
